import React, { useState, useCallback, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Snackbar,
  CircularProgress,
  Alert,
  Tab,
  Typography,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';

import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import Header from '../../components/Header';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { useLocaleText } from 'src/helper/localeText';
import AddStudentDialog from 'src/components/dialogs/student/addStudentDialog';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { useParams } from 'react-router-dom';
import './index.css';
import { ActiveModesContext } from 'src/context/ActiveModesContext';
import {
  FLAG_FINALE,
  FLAG_REZULTATI,
  FLAG_REZULTATI_SKOLA_G,
  FLAG_ZALBE,
  FLAG_ZABRANA
} from 'src/constants/competitions';
import {
  StyledTabs,
  TabsWrapper
} from '../../components/styledHelpComponents/styledHelpComponents';
import { latinToCyrillic } from 'src/helper/helperFunction';
import { Edit, Delete } from '@mui/icons-material';
import ImportStudentsDialog from './administration/helper/ImportStudentsDialog';
import { decodeToken, getToken, isSchool } from 'src/services/authService';
import CustomModal from 'src/components/dialogs/custom/CustomModal';

/* STUDENT STRANA ZA SKOLU */
const Students = ({ schoolId, studentsList }) => {
  const [students, setStudents] = useState(studentsList ? studentsList : []);
  const [categories, setCategories] = useState({});
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');

  // Za import
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [loggedInSchool, setLoggedInSchool] = useState(null);
  const handleCloseImportDialog = () => setOpenImportDialog(false);

  const { t } = useTranslation();
  const localeText = useLocaleText();
  const { schoolIdRef } = useParams();
  const [isEditOrDeleteDisabled, setIsEditOrDeleteDisabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [openCustomDialog, setOpenCustomDialog] = useState(false);

  // Novi state za efektivni ID škole
  const [effectiveSchoolId, setEffectiveSchoolId] = useState(null);

  // Provera pristupa i postavljanje effectiveSchoolId
  useEffect(() => {
    if (isSchool()) {
      const decodedToken = decodeToken();
      const loggedInSchoolId = decodedToken?.school_id;

      if (schoolIdRef) {
        if (loggedInSchoolId === schoolIdRef) {
          setEffectiveSchoolId(schoolIdRef);
        } else {
          setDialogMessage('Nemate dozvolu za pristup podacima druge škole.');
          setOpenCustomDialog(true);
          return;
        }
      } else {
        setEffectiveSchoolId(loggedInSchoolId);
      }
    } else {
      setEffectiveSchoolId(schoolIdRef || schoolId || null);
    }
  }, [schoolIdRef, schoolId]);

  const handleCloseCustomDialog = () => {
    setOpenCustomDialog(false);
  };

  // Active modes
  const { activeModes } = useContext(ActiveModesContext);
  useEffect(() => {
    const isDisabled =
      activeModes.includes(FLAG_ZALBE) ||
      activeModes.includes(FLAG_REZULTATI_SKOLA_G) ||
      activeModes.includes(FLAG_REZULTATI) ||
      activeModes.includes(FLAG_FINALE) ||
      activeModes.includes(FLAG_ZABRANA);

    setIsEditOrDeleteDisabled(isDisabled);
  }, [activeModes]);

  const fetchStudentsAndCategories = useCallback(async () => {
    if (!effectiveSchoolId) return;

    try {
      const token = getToken();

      const studentsUrl = `${API_BASE_URL}/api/students.php?get_all_school_id=${effectiveSchoolId}`;

      // Dodaj token u headers
      const [studentsResponse, categoriesResponse] = await Promise.all([
        axios.get(studentsUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }),
        axios.get(`${API_BASE_URL}/api/categories.php`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      ]);

      const decodedStudents = studentsResponse.data.map((student) => ({
        ...student,
        name: decodeUnicode(student.name),
        professor: decodeUnicode(student.professor),
        gender: decodeUnicode(student.pol) === 'm' ? 'Мушки' : 'Женски',
        language: decodeUnicode(student.language)
      }));

      const decodedCategories = {};
      categoriesResponse.data.forEach((category) => {
        decodedCategories[category.id] = decodeUnicode(category.notes);
      });

      setStudents(decodedStudents);
      setCategories(decodedCategories);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setDialogMessage('Nemate dozvolu za pristup podacima druge škole.');
        setOpenCustomDialog(true);
      } else {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      }
    }
  }, [effectiveSchoolId]);

  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        const decodedToken = decodeToken();
        const loggedInSchoolId = decodedToken?.school_id;

        const response = await axios.get(
          `${API_BASE_URL}/api/schools.php?id=${loggedInSchoolId}`
        );

        if (response.status === 200 && response.data) {
          const schoolData = response.data;
          setLoggedInSchool(schoolData);
        } else {
          console.error('Invalid response:', response);
        }
      } catch (error) {
        console.error('Error fetching school data:', error);
      }
    };

    fetchSchoolData();
  }, []);

  useEffect(() => {
    if (effectiveSchoolId !== null) {
      fetchStudentsAndCategories();
    }
  }, [fetchStudentsAndCategories, effectiveSchoolId]);

  const decodeUnicode = (str) => {
    return str.replace(/\\u[\dA-F]{4}/gi, (match) =>
      String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
    );
  };

  const handleOpenImportDialog = async () => {
    setOpenImportDialog(true);
  };

  const handleOpenDialog = () => {
    setSelectedStudent(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleOpenConfirmDeleteDialog = (student) => {
    setStudentToDelete(student);
    setOpenConfirmDeleteDialog(true);
  };

  const handleCloseConfirmDeleteDialog = () => {
    setOpenConfirmDeleteDialog(false);
    setStudentToDelete(null);
  };

  const handleExportToExcel = () => {
    const exportData = filteredStudents.map((student) => ({
      ID: student.id,
      Ime: student.name,
      Pol: student.gender,
      Razred: student.class,
      Profesor: student.professor,
      Jezik: student.language,
      Kategorija: categories[student.category] || student.category
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Učenici');

    XLSX.writeFile(workbook, 'ucenici.xlsx');
  };

  const handleDeleteStudent = () => {
    axios
      .delete(`${API_BASE_URL}/api/students.php?id=${studentToDelete.id}`)
      .then(() => {
        setSnackbarMessage(t('alerts.studentDeleted'));
        setSnackbarOpen(true);
        fetchStudentsAndCategories();
      })
      .catch((error) => console.error('Error deleting student:', error))
      .finally(() => handleCloseConfirmDeleteDialog());
  };

  const handleEditClick = (student) => {
    setSelectedStudent(null);

    setTimeout(() => {
      setSelectedStudent({ ...student });
      setOpenDialog(true);
    }, 0);
  };

  const handleAddStudentSuccess = () => {
    setLoading(true);
    setSnackbarMessage(t('alerts.studentAdded'));
    setSnackbarOpen(true);
    fetchStudentsAndCategories();
    setLoading(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  const filteredStudents = students
    .filter(
      (student) =>
        selectedTab === 'all' ||
        parseInt(student.class, 10) === parseInt(selectedTab, 10)
    )
    .filter((student) => {
      const latinSearch = searchQuery.toLowerCase();
      const cyrillicSearch = latinToCyrillic(searchQuery).toLowerCase();

      // Poredi ime i profesora u oba formata (latinični i ćirilični)
      return (
        student.name.toLowerCase().includes(latinSearch) ||
        student.name.toLowerCase().includes(cyrillicSearch) ||
        student.professor.toLowerCase().includes(latinSearch) ||
        student.professor.toLowerCase().includes(cyrillicSearch)
      );
    });

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const columns = [
    { field: 'id', headerName: t('columns.id'), width: 90 },
    {
      field: 'name',
      headerName: t('columns.name'),
      width: 200,
      renderCell: (params) => params.row.name
    },
    { field: 'gender', headerName: t('columns.gender'), width: 100 },
    // { field: 'result', headerName: t('columns.result'), width: 100 },
    {
      field: 'category',
      headerName: t('columns.category'),
      width: 200,
      valueGetter: (params) =>
        categories[parseInt(params.row.category)] || params.row.category
    },
    { field: 'class', headerName: t('columns.class'), width: 100 },
    { field: 'professor', headerName: t('columns.professor'), width: 200 },
    {
      field: 'language',
      headerName: t('columns.language'),
      width: 180,
      renderCell: (params) => {
        const language = params.row.language;
        switch (language) {
          case 'С':
            return t('dialog.serbian');
          case 'Х':
            return t('dialog.croatian');
          case 'М':
            return t('dialog.hungarian');
          case 'Л':
            return t('dialog.latin');
          case 'Р':
            return t('dialog.romanian');
          default:
            return language;
        }
      }
    },
    !isEditOrDeleteDisabled
      ? {
          field: 'actions',
          type: 'actions',
          headerName: t('columns.actions'),
          width: 150,
          getActions: (params) => [
            <GridActionsCellItem
              icon={<Edit sx={{ color: '#1c75bc', fontSize: '24px' }} />}
              label="Edit"
              onClick={() => handleEditClick(params.row)}
              disabled={isEditOrDeleteDisabled}
            />,
            <GridActionsCellItem
              icon={<Delete sx={{ color: '#e91e63', fontSize: '24px' }} />}
              label="Delete"
              onClick={() => handleOpenConfirmDeleteDialog(params.row)}
              disabled={isEditOrDeleteDisabled}
            />
          ]
        }
      : {}
  ];

  if (error) {
    return (
      <Box m="20px">
        <Header
          title={t('meni.students')}
          subtitle={t('meni.listofStudents')}
        />
        <Box m="40px 0 0 0">
          <p>{error}</p>
        </Box>
      </Box>
    );
  }

  if (openCustomDialog) {
    return (
      <CustomModal
        open={openCustomDialog}
        onClose={handleCloseCustomDialog}
        title={t('review.notificationTitle')}
        message={dialogMessage}
      />
    );
  }

  return (
    <Box m="20px">
      <Box
        m="20px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          {!isEditOrDeleteDisabled && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenDialog}
              disabled={isEditOrDeleteDisabled}
              sx={{
                marginBottom: '20px',
                backgroundColor: isEditOrDeleteDisabled ? '#cccccc' : '#009d74',
                '&:hover': {
                  backgroundColor: isEditOrDeleteDisabled
                    ? '#cccccc'
                    : '#1c75bc'
                },
                color: '#ffffff',
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: 'bold'
              }}
            >
              {t('meni.addNewStudent')}
            </Button>
          )}
          {!isEditOrDeleteDisabled && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenImportDialog}
              disabled={isEditOrDeleteDisabled}
              sx={{
                marginLeft: '10px',
                marginBottom: '20px',
                backgroundColor: '#ff2626e0',
                '&:hover': {
                  backgroundColor: isEditOrDeleteDisabled
                    ? '#cccccc'
                    : '#1c75bc'
                },
                color: '#ffffff',
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: 'bold'
              }}
            >
              {t('meni.importstudents')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportToExcel}
            sx={{
              marginLeft: '10px',
              marginBottom: '20px',
              backgroundColor: '#1c75bc',
              '&:hover': { backgroundColor: '#155a8a' },
              color: '#ffffff',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: 'bold'
            }}
          >
            {t('result.exportExcel')}
          </Button>
        </Box>

        {/* Link za preuzimanje šablona, poravnat desno */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 'bold',
            color: '#333'
          }}
        >
          <a
            href="/kengur2/assets/prijavni_list.xlsx"
            download
            style={{
              color: '#1c75bc',
              // textDecoration: 'none',
              fontSize: '12px'
            }}
            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
          >
            {t('links.downloadTemplate')}
          </a>
        </Typography>
      </Box>

      {isEditOrDeleteDisabled && (
        <Box
          sx={{
            marginBottom: '20px',
            backgroundColor: '#ffe0e0',
            color: '#d32f2f',
            padding: '10px 20px',
            borderRadius: '8px',
            textAlign: 'center',
            fontWeight: 'bold',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #d32f2f'
          }}
        >
          {t('alerts.submissionClosed')}
        </Box>
      )}
      <Typography
        variant="h5"
        sx={{
          marginBottom: '10px',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'left'
        }}
      >
        {t('headers.enrolledStudents')}
      </Typography>
      <TabsWrapper>
        <StyledTabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label={t('results.tabsLabel')}
          sx={{
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '0px'
          }}
        >
          <Tab label={t('results.allClasses')} value="all" />
          {[...Array(12).keys()].map((num) => (
            <Tab
              label={t('results.grade', { grade: num + 1 })}
              value={num + 1}
              key={num}
              sx={{
                fontWeight:
                  selectedTab === (num + 1).toString() ? 'bold' : 'normal',
                backgroundColor:
                  selectedTab === (num + 1).toString() ? '#4facfe' : '#ffffff',
                color:
                  selectedTab === (num + 1).toString() ? '#ffffff' : '#333',
                '&:hover': {
                  backgroundColor:
                    selectedTab === (num + 1).toString() ? '#1c75bc' : '#f0f0f0'
                },
                borderRadius: '8px',
                transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s'
              }}
            />
          ))}
        </StyledTabs>
      </TabsWrapper>
      <TextField
        variant="outlined"
        placeholder={t('search.placeholder')}
        value={searchQuery}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{
          width: '100%',
          backgroundColor: '#f5f7fa',
          borderRadius: '8px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px'
          },
          marginBottom: '20px'
        }}
      />
      <Box
        m="0px 0 0 0"
        height={schoolId ? '35vh' : '75vh'}
        sx={{ backgroundColor: '#ffffff' }}
      >
        <DataGrid
          rows={filteredStudents}
          columns={columns}
          localeText={localeText}
          className="students-data-grid"
          disableColumnMenu
          componentsProps={{
            pagination: {
              labelRowsPerPage: t('pagination.rowsPerPage'),
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} ${t('pagination.of')} ${
                  count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
                }`
            }
          }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '10px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              padding: '12px',
              fontSize: '14px',
              color: '#333'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#2596be',
              color: '#ffffff',
              borderBottom: 'none',
              borderRadius: '12px 12px 0 0'
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#2596be',
              color: '#ffff',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            },
            '& .MuiCheckbox-root': {
              color: '#28a745 !important'
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: '#ffff !important'
            }
          }}
        />
      </Box>
      <AddStudentDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        schoolId={effectiveSchoolId}
        studentData={selectedStudent}
        onSuccess={handleAddStudentSuccess}
      />
      {openImportDialog && (
        <ImportStudentsDialog
          open={openImportDialog}
          handleClose={handleCloseImportDialog}
          isSchool={true}
          loggedInSchool={loggedInSchool}
          onSuccess={() => {
            fetchStudentsAndCategories();
            setSnackbarMessage(t('alerts.studentsImported'));
            setSnackbarOpen(true);
          }}
        />
      )}
      <ConfirmDeleteDialog
        open={openConfirmDeleteDialog}
        onClose={handleCloseConfirmDeleteDialog}
        onConfirm={handleDeleteStudent}
      />
      {loading && (
        <CircularProgress
          sx={{ display: 'block', margin: '20px auto', color: '#4facfe' }}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={10000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ borderRadius: '8px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Students;
